import { scoreScopeLabel, shareModeLabel } from "../lib/shareCard";
import type { SetupStatKey } from "../lib/setupStats";
import type { Horizon, Mode, Timeframe } from "../lib/rounds";
import { useChallengeStore } from "./challengeStore";
import type { Score, Status, Streak } from "./roundLoop";
import { usePredictDailyStore } from "./predictDailyStore";
import { usePredictMonthlyStore } from "./predictMonthlyStore";
import { usePredictDailyNextStore } from "./predictNextCandleStores";
import {
  usePredictMonthlyTrendStore,
  usePredictWeeklyTrendStore,
} from "./predictTrendStores";
import { usePredictWeeklyStore } from "./predictWeeklyStore";
import { useSettingsStore } from "./settingsStore";
import { useTradeStore } from "./tradeStore";

/**
 * The one seam for "which Loop is active, and what does it use" (CONTEXT.md
 * "Loop"). Everywhere that used to re-derive the active Loop from mode +
 * timeframe (App's routing, the share control, the settings strip) reads a
 * LoopDescriptor from here instead.
 *
 * A Predict Loop is a (timeframe, horizon) pair, and since ADR-0013 every
 * bank carries a Forward path, so all six pairs exist. Trade takes the
 * timeframe setting too but no horizon, and the Daily challenge takes neither.
 */

export type PredictLoopId =
  | "predict:daily:nextCandle"
  | "predict:daily:trend"
  | "predict:weekly:nextCandle"
  | "predict:weekly:trend"
  | "predict:monthly:nextCandle"
  | "predict:monthly:trend";

export type LoopId = PredictLoopId | "trade" | "challenge";

/**
 * The one derivation of the active Loop from settings. Timeframe and horizon
 * are Predict-only, and every (timeframe, horizon) pair has a Loop (ADR-0013),
 * so the mapping is total with nothing to fall back to.
 */
export function activeLoopId(
  mode: Mode,
  timeframe: Timeframe,
  horizon: Horizon,
): LoopId {
  if (mode === "trade") return "trade";
  // The Daily challenge fixes its own timeframe and horizon (GRA-139), so
  // neither setting routes within it.
  if (mode === "challenge") return "challenge";
  return `predict:${timeframe}:${horizon}` as LoopId;
}

/**
 * Whether a settings row applies to a Loop. "hidden" drops the row from the
 * strip entirely rather than rendering it disabled: difficulty and Context
 * length have no effect at all on a next-candle guess (GRA-132, reversing the
 * earlier "disabled, not hidden" call for these two rows), so leaving them
 * on screen just clutters the strip with controls that do nothing this Loop.
 * The stored values are untouched either way, so they are exactly as the
 * player left them if the row comes back on a later Loop switch.
 */
export type SettingAvailability = "on" | "hidden";

/**
 * Which settings rows apply to a Loop (drives SettingsBar). Markets and accent
 * are not here because they show for every Loop regardless, so there is nothing
 * for a per-Loop flag to decide.
 */
export type LoopSettingsFlags = {
  /** The guess-horizon control (Predict only). */
  horizon: boolean;
  /** The timeframe segmented control (Predict only). */
  timeframe: boolean;
  /** Difficulty preset: live for the trend game, hidden for next-candle. */
  difficulty: SettingAvailability;
  /** Context length: live for Trade and the trend game, hidden for next-candle. */
  contextLength: SettingAvailability;
};

/**
 * A read-only view of a scored Loop's run, narrow enough that every Loop's
 * store satisfies it whatever its Round/commit/reveal types. Lets the header
 * and the share control subscribe to *the active* Loop with one hook call
 * instead of one per Loop.
 */
export type ScoreSource = <T>(
  selector: (state: { score: Score; streak: Streak }) => T,
) => T;

export type LoopDescriptor = {
  id: LoopId;
  /** Share-card label ("Predict · daily · trend", "Trade"). */
  shareLabel: string;
  /**
   * The header score readout's scope label ("Trend · Daily"), or null for
   * Trade (GRA-128: Trade's readout carries no timeframe/horizon to scope).
   */
  scopeLabel: string | null;
  /** The Loop's scored run, or null; Trade is scored on balance, not hit rate. */
  scoreboard: ScoreSource | null;
  /** Which settings rows apply to this Loop. */
  settings: LoopSettingsFlags;
  /** The Loop's lifecycle surface App routes through. */
  status: () => Status;
  init: () => Promise<void>;
};

const TREND_SETTINGS: LoopSettingsFlags = {
  horizon: true,
  timeframe: true,
  difficulty: "on",
  contextLength: "on",
};

const NEXT_CANDLE_SETTINGS: LoopSettingsFlags = {
  horizon: true,
  timeframe: true,
  difficulty: "hidden",
  contextLength: "hidden",
};

/** Build a Predict descriptor from its (timeframe, horizon) pair and its store. */
function predictLoop(
  timeframe: Timeframe,
  horizon: Horizon,
  store: ScoreSource & { getState: () => { status: Status; init: () => Promise<void> } },
): LoopDescriptor {
  return {
    id: `predict:${timeframe}:${horizon}` as PredictLoopId,
    shareLabel: shareModeLabel("predict", timeframe, horizon),
    scopeLabel: scoreScopeLabel(timeframe, horizon),
    scoreboard: store,
    settings: horizon === "trend" ? TREND_SETTINGS : NEXT_CANDLE_SETTINGS,
    status: () => store.getState().status,
    init: () => store.getState().init(),
  };
}

export const LOOPS: Record<LoopId, LoopDescriptor> = {
  "predict:daily:trend": predictLoop("daily", "trend", usePredictDailyStore),
  "predict:daily:nextCandle": predictLoop(
    "daily",
    "nextCandle",
    usePredictDailyNextStore,
  ),
  "predict:weekly:trend": predictLoop(
    "weekly",
    "trend",
    usePredictWeeklyTrendStore,
  ),
  "predict:weekly:nextCandle": predictLoop(
    "weekly",
    "nextCandle",
    usePredictWeeklyStore,
  ),
  "predict:monthly:trend": predictLoop(
    "monthly",
    "trend",
    usePredictMonthlyTrendStore,
  ),
  "predict:monthly:nextCandle": predictLoop(
    "monthly",
    "nextCandle",
    usePredictMonthlyStore,
  ),
  challenge: {
    id: "challenge",
    shareLabel: shareModeLabel("challenge", "daily", "trend"),
    // No header hit rate: the challenge's readout is its progress through the
    // run (ChallengeProgress), and its score is the run's own, not a lifetime
    // one. `scoreboard: null` is the same "scored elsewhere" answer Trade
    // gives, for the same reason.
    scopeLabel: null,
    scoreboard: null,
    // Every play setting is fixed by the challenge itself, so the whole
    // strip's game rows are hidden; only Markets would remain, and the
    // challenge view passes an empty market list to drop that too.
    settings: {
      horizon: false,
      timeframe: false,
      difficulty: "hidden",
      contextLength: "hidden",
    },
    status: () => useChallengeStore.getState().status,
    init: () => useChallengeStore.getState().init(),
  },
  trade: {
    id: "trade",
    shareLabel: shareModeLabel("trade", "daily", "trend"),
    scopeLabel: null,
    scoreboard: null,
    settings: {
      horizon: false,
      // Trade plays every timeframe (ADR-0013), so the row shows for it, and
      // tradeStore reads the setting for both its bank and its filter.
      timeframe: true,
      difficulty: "hidden",
      contextLength: "on",
    },
    status: () => useTradeStore.getState().status,
    init: () => useTradeStore.getState().init(),
  },
};

/** The active Loop's descriptor, read from the settings store. */
export function useActiveLoop(): LoopDescriptor {
  const mode = useSettingsStore((s) => s.mode);
  const timeframe = useSettingsStore((s) => s.timeframe);
  const horizon = useSettingsStore((s) => s.horizon);
  return LOOPS[activeLoopId(mode, timeframe, horizon)];
}

/**
 * The setup-stats bucket the active Loop's own reveal reads (GRA-151): its
 * horizon, timeframe, and whichever preset is currently selected (a
 * next-candle bucket ignores the preset anyway, so it is harmless to pass
 * one regardless). Null for Trade and the Daily challenge, which have no
 * setup-stats bucket of their own to benchmark against in the status bar.
 */
export function useActiveStatKey(): SetupStatKey | null {
  const mode = useSettingsStore((s) => s.mode);
  const timeframe = useSettingsStore((s) => s.timeframe);
  const horizon = useSettingsStore((s) => s.horizon);
  const preset = useSettingsStore((s) => s.preset);
  if (mode !== "predict") return null;
  return { horizon, timeframe, preset };
}
